import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Authorization from 'components/global/Authorization';
import Collapse from 'components/global/Collapse';
import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import { PAYMENT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS from 'utils/paths';
import { formatPrice } from 'utils/utils';

import { faAngleUp, faCircleCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';

import * as S from './Tabs.styled';

const OrderSummary = ({ goToSuccessTab }) => {
	const { t } = useTranslation();
	const formMethods = useFormContext();
	const { Currency, LangCountryCode } = useLocale();
	const { CODRemainingAmount } = useSelector((state) => state.user);

	const isMobile = useMediaQuery(Breakpoints.mobile);
	const [isAccordionVisible, setIsAccordionVisible] = useState(false);

	const { watch, setValue } = formMethods;

	const total = watch('total');
	const codWallet = watch('codWallet');

	const handleRemoveCodWallet = () => {
		setValue('codWallet', null);
	};

	useEffect(() => {
		if (total < codWallet) {
			setValue('codWallet', null);
		}
	}, [total]);

	if (isMobile) {
		return (
			<S.MobileContainer $isAccordionVisible={isAccordionVisible}>
				<Collapse defaultVisible={false} visible={isAccordionVisible}>
					<S.MobileSummaryContent>
						<Typography fontWeight="700" fontSize="var(--paragraph2-font-size)">
							orderSummary
						</Typography>
						<S.SummaryRow>
							<Typography color="var(--content-3)">subtotal</Typography>
							<Typography color="var(--content-3)" withCurrency>
								{watch('subTotal')}
							</Typography>
						</S.SummaryRow>
						<S.SummaryRow>
							<Typography color="var(--content-3)">vatAmount</Typography>
							<Typography color="var(--content-3)" withCurrency>
								{watch('vatAmount')}
							</Typography>
						</S.SummaryRow>
						{codWallet && (
							<S.FlexBox $justifyContent="space-between" $alignItems="flex-start">
								<S.FlexBox $flexDirection="column">
									<S.FlexBox>
										<Typography color="var(--content-3)" fontSize="var(--paragraph2-font-size)">
											codWallet
										</Typography>
										<S.CODBadge>
											<Typography variant="caption" fontWeight="500">
												toBeDeducted
											</Typography>
										</S.CODBadge>
									</S.FlexBox>
									<Typography color="var(--content-3)" fontSize="var(--paragraph3-font-size)">
										<Trans
											i18nKey="remainingBalanceWithCurrency"
											values={{
												value: CODRemainingAmount - codWallet,
												currency: t(Currency)
											}}
											components={{
												blackText: <span style={{ fontWeight: 700 }} />
											}}
										/>
									</Typography>
								</S.FlexBox>

								<S.FlexBox $width="auto" $alignItems="center" $gap={'0'}>
									<Typography
										color="var(--content-3)"
										fontSize="var(--paragraph2-font-size)"
										withCurrency
										css={{ width: 'max-content' }}>
										{-codWallet}
									</Typography>
									<Button
										icon={faXmark}
										onClick={handleRemoveCodWallet}
										variant="transparent"
										size="x-small"
									/>
								</S.FlexBox>
							</S.FlexBox>
						)}
					</S.MobileSummaryContent>
				</Collapse>
				<S.MobileSummaryRow
					onClick={() => setIsAccordionVisible((prev) => !prev)}
					$isAccordionVisible={isAccordionVisible}>
					<S.FlexBox $gap="0.5rem" $width="auto">
						<Typography variant="p2" fontWeight="500">
							{codWallet ? 'newTotal' : 'total'}
						</Typography>
						<S.ExpandIcon
							icon={faAngleUp}
							size="small"
							// attribute expects string
							expanded={isAccordionVisible.toString()}
							color="var(--black)"
						/>
					</S.FlexBox>

					<Typography variant="p2" fontWeight="500">
						<Trans
							i18nKey="chargeAccount.totalAmount"
							values={{
								value: formatPrice(
									codWallet ? +(total - codWallet).toFixed(2) : total,
									LangCountryCode
								),
								currency: t(Currency)
							}}
							components={{
								blackText: <span style={{ fontWeight: 700, fontSize: 'var(--paragraph1-font-size)' }} />
							}}
						/>
					</Typography>
				</S.MobileSummaryRow>
			</S.MobileContainer>
		);
	}

	return (
		<S.Container>
			<Typography fontWeight="700" fontSize="var(--paragraph1-font-size)">
				orderSummary
			</Typography>

			<S.SummaryContent>
				<S.SummaryRow>
					<Typography color="var(--content-3)">subtotal</Typography>
					<Typography withCurrency color="var(--content-3)">
						{watch('subTotal') || 0}
					</Typography>
				</S.SummaryRow>
				<S.SummaryRow>
					<Typography color="var(--content-3)">vatAmount</Typography>
					<Typography withCurrency color="var(--content-3)">
						{watch('vatAmount') || 0}
					</Typography>
				</S.SummaryRow>

				<S.SummaryRow $hasLine={!codWallet}>
					<Typography fontWeight="500" variant="p2" color={codWallet ? 'var(--content-3)' : 'var(--black)'}>
						total
					</Typography>
					<Typography
						fontWeight="500"
						variant="p2"
						withCurrency
						color={codWallet ? 'var(--content-3)' : 'var(--black)'}>
						{total || 0}
					</Typography>
				</S.SummaryRow>

				<CODWalletInput />

				{!!codWallet && (
					<S.SummaryRow $bottomLine>
						<Typography variant="p1" fontWeight="500">
							newTotal
						</Typography>
						<Typography variant="p1" fontWeight="500" withCurrency>
							{total - codWallet}
						</Typography>
					</S.SummaryRow>
				)}

				<S.SummaryRow>
					<PromoCodeInput goToSuccessTab={goToSuccessTab} />
				</S.SummaryRow>
			</S.SummaryContent>
		</S.Container>
	);
};

export const PromoCodeInput = ({ goToSuccessTab }) => {
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { Currency, LangCountryCode } = useLocale();
	const [prevPromoCode, setPrevPromoCode] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const formMethods = useFormContext();
	const { setValue, watch } = formMethods;
	const userData = useSelector((state) => state.user);

	const promoCode = watch('promoCode');

	const applyPromoCode = (data) => {
		setValue('freeCredit', data.freeCredit);
		setValue('isPromoCodeApplied', true);
		setValue('promoCode', promoCode);
	};

	const handleCheckPromoCode = async () => {
		/**
		 * it keeps the previous promo code in state and does not send requests
		 * unless it changes so it won't run multiple times
		 */
		if (!promoCode || promoCode === prevPromoCode) {
			return null; // Return null if the promoCode is empty
		}
		setIsLoading(true);
		setPrevPromoCode(promoCode);
		try {
			const response = await axiosPrivate.post(PAYMENT.CHECK_PROMO_CODE, {
				code: promoCode,
				subtotal: watch('subTotal') || undefined
			});
			const { data, status } = response;
			if (status === 200) {
				if (data.valid === 'true') {
					applyPromoCode(data);

					if (data.type === 'freeCredit') {
						goToSuccessTab();
					} else {
						setPrevPromoCode('');
						showSuccess({
							message: 'successMsg.promoCodeApplied'
						});
						return true; // Return true if the promoCode is valid and not freeCredit
					}
				} else if (data.valid === 'onhold') {
					showError({
						message: 'errorMsg.onHoldPromoCode'
					});
					return false; // Return false if the promoCode is on hold
				} else if (data.valid === 'false') {
					showError({
						message: 'errorMsg.invalidPromoCode'
					});
					window.dataLayer?.push({
						/* eslint-disable camelcase */
						event: 'promocode_redeem_fail',
						first_name: userData?.firstName || '',
						last_name: userData?.lastName || '',
						phone: userData?.phone || '',
						email: userData?.email || '',
						plan: userData?.planName || '',
						country: userData?.localization?.label || '',
						userID: userData?.ID || '',
						promo_code: promoCode,
						fail_reason: 'invalidPromoCode'
						/* eslint-enable camelcase */
					});

					return false; // Return false if the promoCode is invalid
				} else {
					showError();
					AppLogger.error(response);
					return false; // Return false if the response data is not as expected
				}
			} else {
				showError();
				AppLogger.error(response);
				return false; // Return false if the API call fails
			}
		} catch (error) {
			if (error.response?.data.code === 'error.D003') {
				showError({
					message: 'errorMsg.selectAmount'
				});
			} else {
				showError({
					message: error.response?.data.code || error.response?.data.message
				});
				AppLogger.error(error);
			}
			return false; // Return false if an error occurs during the API call
		} finally {
			setIsLoading(false);
		}
	};

	const handleRemoveActivePromoCode = () => {
		setValue('freeCredit', null);
		setValue('isPromoCodeApplied', false);
		setValue('promoCode', null);
		setPrevPromoCode('');
	};

	return (
		<S.PromoCodeContainer>
			<Typography fontWeight="500" fontSize="var(--paragraph2-font-size)">
				promoCode
			</Typography>
			<S.FlexBox>
				<S.InputWithButton>
					<StringInput
						value={promoCode}
						onChange={({ target: { value } }) => setValue('promoCode', value)}
						placeholder="promoCodeString"
						disabled={watch('isPromoCodeApplied')}
					/>
					{watch('isPromoCodeApplied') && (
						<Button
							icon={faXmark}
							onClick={handleRemoveActivePromoCode}
							variant="borderless"
							size="x-small"
							className="input-remove-promo-code"
						/>
					)}
				</S.InputWithButton>
				<Button
					disabledTooltip={'errorMsg.enterPromoCode'}
					disabled={!promoCode || prevPromoCode === promoCode || watch('isPromoCodeApplied')}
					loading={isLoading}
					onClick={handleCheckPromoCode}
					label={watch('isPromoCodeApplied') ? 'applied' : 'applyPromoCode'}
					variant="outline"
				/>
			</S.FlexBox>
			{watch('isPromoCodeApplied') && (
				<S.FlexBox $gap=".5rem">
					<Icon icon={faCircleCheck} color="var(--green)" size="small" />
					<Typography
						variant="caption"
						fontWeight="400"
						color="var(--green)"
						noWrap
						css={{ lineHeight: '2rem' }}>
						<Trans
							i18nKey="appliedPromoCodeWithValue"
							values={{
								freeCredit: `${formatPrice(watch('freeCredit'), LangCountryCode)} ${Currency}`
							}}
							components={{
								boldText: <span style={{ color: 'var(--green)', fontWeight: 700 }} />
							}}
						/>
					</Typography>
				</S.FlexBox>
			)}
		</S.PromoCodeContainer>
	);
};

export const CODWalletInput = () => {
	const { Currency, LangCountryCode } = useLocale();
	const formMethods = useFormContext();
	const { t } = useTranslation();

	const {
		CODRemainingAmount,
		generalSettings: { BankName, BankHolderName, BankIban }
	} = useSelector((state) => state.user);

	const { watch, setValue } = formMethods;

	const [codWalletActive, setCodWalletActive] = useState(false);
	const [codWallet, setCODWallet] = useState(null);

	const handleAppendCodWallet = async () => {
		if (codWallet > CODRemainingAmount || watch('total') < codWallet) {
			return;
		}
		setValue('isCodPaymentSuccess', false);
		setValue('codWallet', codWallet);
	};

	const handleRemoveCodWallet = () => {
		setValue('codWallet', null);
		setCODWallet(null);
	};

	useEffect(() => {
		if (watch('total') < codWallet) {
			handleRemoveCodWallet();
		}
	}, [watch('total')]);

	const isCODWalletShow = useMemo(
		() => CODRemainingAmount > 0 || (BankName && BankHolderName && BankIban),
		[CODRemainingAmount, BankName, BankHolderName, BankIban]
	);

	if (!isCODWalletShow) {
		return null;
	}

	return (
		<Authorization path={PATHS.COD_WALLET}>
			<S.CODWalletContainer>
				<S.SummaryRow $bottomLine $hasLine={watch('codWallet')}>
					{watch('codWallet') ? (
						<S.FlexBox $flexDirection={'column'}>
							<S.FlexBox $justifyContent="space-between" $alignItems="flex-start">
								<S.FlexBox $flexDirection="column">
									<S.FlexBox>
										<Typography color="var(--content-3)" fontSize="var(--paragraph2-font-size)">
											codWallet
										</Typography>
										<S.CODBadge>
											<Typography variant="caption" color="var(--white)" fontWeight="500">
												toBeDeducted
											</Typography>
										</S.CODBadge>
									</S.FlexBox>
									<Typography
										variant="caption"
										color="var(--content-3)"
										fontSize="var(--paragraph3-font-size)">
										<Trans
											i18nKey="remainingBalanceWithCurrency"
											values={{
												value: watch('isCodPaymentSuccess')
													? CODRemainingAmount
													: CODRemainingAmount - watch('codWallet'),
												currency: t(Currency)
											}}
											components={{
												blackText: <S.BlackText />
											}}
										/>
									</Typography>
								</S.FlexBox>

								<S.FlexBox $width="auto" $alignItems="center" $gap={'0'}>
									<Typography
										color="var(--content-3)"
										fontSize="var(--paragraph2-font-size)"
										withCurrency
										style={{ whiteSpace: 'nowrap' }}>
										{-watch('codWallet')}
									</Typography>
									<Button
										icon={faXmark}
										onClick={handleRemoveCodWallet}
										variant="transparent"
										size="x-small"
										css={{ padding: '0 !important' }}
									/>
								</S.FlexBox>
							</S.FlexBox>
							{watch('isCodPaymentSuccess') && (
								<S.FlexBox $gap=".5rem">
									<Icon icon={faCircleCheck} color="var(--green)" size="small" />
									<Typography
										variant="caption"
										fontWeight="400"
										color="var(--green)"
										noWrap
										css={{ lineHeight: '2rem' }}>
										<Trans
											i18nKey="appliedCODWalletWithValue"
											values={{
												freeCredit: `${formatPrice(watch('codWallet'), LangCountryCode)} ${Currency}`
											}}
											components={{
												boldText: <span style={{ color: 'var(--green)', fontWeight: 700 }} />
											}}
										/>
									</Typography>
								</S.FlexBox>
							)}
						</S.FlexBox>
					) : (
						<S.FlexBox $flexDirection="column">
							<S.FlexBox>
								<SwitchInput
									value={codWalletActive}
									setValue={() => setCodWalletActive(!codWalletActive)}
									className="cod-wallet-switch"
								/>
								<S.FlexBox $flexDirection="column">
									<Typography fontWeight="500" fontSize="var(--paragraph2-font-size)">
										codWallet
									</Typography>
									<Typography variant="caption" color="var(--content-3)">
										<Trans
											i18nKey="chargeAccount.availableBalanceWithValue"
											values={{
												value: CODRemainingAmount,
												currency: t(Currency)
											}}
											components={{
												blackText: <S.BlackText />
											}}
										/>
									</Typography>
								</S.FlexBox>
							</S.FlexBox>

							{codWalletActive && (
								<S.CODWalletContainer>
									<IntegerInput
										value={codWallet}
										onChange={({ target: { value } }) => {
											const val = Math.abs(value);
											if (Number.isNaN(val) || val > CODRemainingAmount || watch('total') < val) {
												return;
											}

											setCODWallet(val || null);
										}}
										disabled={watch('total') < 1 || CODRemainingAmount === 0}
										placeholder="codWallet"
										unit={Currency}
										showArrows={false}
										decimalLimit={2}
										allowFloat
									/>
									<Button
										onClick={handleAppendCodWallet}
										label="applyCodWallet"
										variant="outline"
										disabled={watch('total') < 1 || CODRemainingAmount === 0}
									/>
								</S.CODWalletContainer>
							)}
						</S.FlexBox>
					)}
				</S.SummaryRow>
			</S.CODWalletContainer>
		</Authorization>
	);
};

export default OrderSummary;
